import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api, mediaUrl } from "../lib/api";
import { scopeCSS } from "../components/landing/scopeCSS";
import { STYLES } from "../components/articles/articles-styles";
import { mountField } from "../components/articles/trex-field";
import { genCoverStyle, seriesLabel } from "../components/articles/genCover";

const HOST = "trex-articles";

const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Articles() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [series, setSeries] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);
  const [swap, setSwap] = useState(false);
  const [stuck, setStuck] = useState(false);

  const canvasRef = useRef(null);
  const rowRef = useRef(null);
  const inkRef = useRef(null);
  const gridRef = useRef(null);
  const sentinelRef = useRef(null);

  const css = useMemo(() => scopeCSS(STYLES, HOST), []);

  useEffect(() => {
    Promise.all([
      api.get("/categories").then((r) => r.data.categories),
      api.get("/articles", { params: { limit: 100 } }).then((r) => r.data.articles),
    ])
      .then(([cats, arts]) => {
        setArticles(arts);
        const used = cats.filter((c) => arts.some((a) => a.category === c));
        const extra = [...new Set(arts.map((a) => a.category))].filter((c) => c && !cats.includes(c));
        setSeries([...used, ...extra]);
      })
      .finally(() => setLoading(false));
  }, []);

  // hero shader
  useEffect(() => {
    if (!canvasRef.current) return;
    const field = mountField(canvasRef.current, "hero");
    return () => field.destroy();
  }, []);

  // sticky hairline on the filter bar
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { threshold: 1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const list = useMemo(
    () =>
      articles
        .filter((a) => active === "All" || a.category === active)
        .sort((a, b) => String(b.published_at || b.created_at || "").localeCompare(String(a.published_at || a.created_at || ""))),
    [articles, active]
  );

  // the sliding indicator follows the pressed pill
  const moveInk = () => {
    const row = rowRef.current, ink = inkRef.current;
    if (!row || !ink) return;
    const b = row.querySelector('.pill[aria-pressed="true"]');
    if (!b) return;
    ink.style.width = `${b.offsetWidth}px`;
    ink.style.transform = `translateX(${b.offsetLeft}px)`;
  };

  useEffect(() => {
    moveInk();
    const onResize = () => moveInk();
    window.addEventListener("resize", onResize);
    document.fonts?.ready.then(moveInk).catch(() => {});
    return () => window.removeEventListener("resize", onResize);
  }, [active, series, loading]);

  // cards rise in as they enter the viewport
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const cards = [...grid.querySelectorAll(".card")];
    const showVisible = () => {
      const h = window.innerHeight;
      cards.forEach((c) => {
        const r = c.getBoundingClientRect();
        if (r.top < h * 0.96 && r.bottom > 0) c.classList.add("show");
      });
    };
    if (!("IntersectionObserver" in window)) {
      cards.forEach((c) => c.classList.add("show"));
      return;
    }
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("show"); io.unobserve(e.target); } }),
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );
    const raf = requestAnimationFrame(() => {
      showVisible();
      cards.forEach((c) => { if (!c.classList.contains("show")) io.observe(c); });
    });
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [list]);

  const pick = (c) => {
    if (c === active) return;
    setSwap(true);
    setTimeout(() => { setActive(c); setSwap(false); }, 220);
  };

  const open = (e, slug) => {
    e.preventDefault();
    navigate(`/articles/${slug}`);
  };

  return (
    <main data-testid="articles-page">
      <Helmet>
        <title>Articles | T-REX Network</title>
        <meta name="description" content="Research, market reading and product news from T-REX Network: After Issuance, the Tokenization Digest, partner announcements and perspectives." />
        <meta property="og:title" content="Articles | T-REX Network" />
        <meta property="og:description" content="Research, market reading and product news from the team building the shared market infrastructure for tokenized securities." />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />
      {/* Scoped stylesheet for the hand-built articles design. Developer-authored
          string constant, never user input. */}
      <style data-trex-host="" dangerouslySetInnerHTML={{ __html: css }} />

      <div className={HOST}>
        <header className="hero">
          <canvas ref={canvasRef} id="field" aria-hidden="true" />
          <div className="veil" aria-hidden="true" />
          <div className="wrap">
            <h1 className="in">The latest from us</h1>
            <p className="in">
              Research, market reading and news from the team building the shared market infrastructure for tokenized securities.
            </p>
          </div>
        </header>

        <div ref={sentinelRef} />
        <div className={`filters${stuck ? " stuck" : ""}`} id="filters">
          <div className="wrap">
            <div className="scroll">
              <div className="row" ref={rowRef} role="group" aria-label="Filter by series">
                <span className="ink" ref={inkRef} />
                {["All", ...series].map((c) => (
                  <button
                    key={c}
                    className="pill"
                    type="button"
                    aria-pressed={c === active}
                    data-testid={`articles-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => pick(c)}
                  >
                    {c}
                    <span className="n">{c === "All" ? articles.length : articles.filter((a) => a.category === c).length}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <section className="feed">
          <h2 className="sr">Articles</h2>
          {loading ? (
            <div className="load" data-testid="articles-loading"><i /></div>
          ) : list.length === 0 ? (
            <div className="empty" data-testid="articles-empty">
              <span>Nothing here yet</span>Choose another series above.
            </div>
          ) : (
            <ul className={`grid${swap ? " swap" : ""}`} ref={gridRef} aria-live="polite">
              {list.map((a, i) => (
                <li
                  className="card"
                  key={a.article_id || a.slug}
                  data-c={a.category}
                  data-testid={`article-card-${a.slug}`}
                  style={{ transitionDelay: `${80 + Math.min(i, 8) * 55}ms` }}
                >
                  <div className="shot">
                    {a.cover_image ? (
                      <img src={mediaUrl(a.cover_image)} alt="" loading={i < 3 ? "eager" : "lazy"} width="1400" height="560" />
                    ) : (
                      <div className="gen" style={{ background: genCoverStyle(a.title) }}>
                        <span className="series" dangerouslySetInnerHTML={{ __html: seriesLabel(a.category) }} />
                        <span className="tag">{a.category}</span>
                      </div>
                    )}
                  </div>
                  <div className="body">
                    <div className="meta">
                      <span className="cat">{a.category}</span>
                      <span className="dot" />
                      <time className="date" dateTime={a.published_at || a.created_at}>
                        {fmtDate(a.published_at || a.created_at)}
                      </time>
                    </div>
                    <h2>{a.card_title || a.title}</h2>
                    <p>{a.card_excerpt || a.excerpt}</p>
                    <span className="go">
                      <span className="line" aria-hidden="true"><i /><b /></span>Read
                    </span>
                  </div>
                  <a className="hit" href={`/articles/${a.slug}`} onClick={(e) => open(e, a.slug)} aria-label={a.card_title || a.title} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <Footer />
    </main>
  );
}
